/**
 * Completing a request: what the department found or gave.
 *
 * Three shapes, the same three the server keeps (routes/requests.js
 * sanitiseResult): the lab's values table, radiology's report and impression,
 * and what the pharmacy dispensed. Whatever is entered here is the patient's
 * medical file the moment it is saved — there is no second copy to update —
 * and the request moves on to Accounts to be priced.
 */

import { useRef, useState } from 'react';
import { Badge, Button, Field, Icon, Input, Modal, Select, Textarea } from '../../components/common';
import { patientsApi, requestsApi } from '../../api/endpoints';
import { useNotifications } from '../../context/NotificationContext';
import { readFileAsDataUrl } from './labels';

const FLAGS = [
  { id: '', label: '—' },
  { id: 'normal', label: 'طبيعي' },
  { id: 'high', label: 'مرتفع' },
  { id: 'low', label: 'منخفض' },
  { id: 'critical', label: 'حرج' },
];

const blankValue = (test = '') => ({ test, value: '', unit: '', range: '', flag: '' });

function initialFor(request) {
  const items = Array.isArray(request.items) ? request.items : [];
  if (request.department === 'lab') {
    return { summary: '', values: items.length ? items.map((i) => blankValue(i.name)) : [blankValue()] };
  }
  if (request.department === 'radiology') return { impression: '', report: '' };
  if (request.department === 'pharmacy' || request.department === 'medication') {
    return {
      summary: '',
      dispensed: items.length
        ? items.map((i) => ({ name: i.name, quantity: String(i.quantity || 1), instructions: i.instructions || '' }))
        : [{ name: '', quantity: '1', instructions: '' }],
    };
  }
  return { summary: '' };
}

export function ResultForm({ request, open, onClose, onDone }) {
  const { success, error: toastError } = useNotifications();
  const [result, setResult] = useState(() => initialFor(request));
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const picker = useRef(null);

  const canAttach = request.department === 'lab' || request.department === 'radiology';

  const setRow = (key, index, patch) =>
    setResult((r) => ({ ...r, [key]: r[key].map((row, i) => (i === index ? { ...row, ...patch } : row)) }));

  const pick = (event) => {
    const chosen = Array.from(event.target.files || []);
    event.target.value = '';
    const tooBig = chosen.find((f) => f.size > 8 * 1024 * 1024);
    if (tooBig) return toastError(`${tooBig.name}: أكبر من 8 ميجابايت`);
    setFiles((list) => [...list, ...chosen].slice(0, 10));
  };

  const complete = async () => {
    setBusy(true);
    try {
      // Files first: the request records their ids, so they must exist.
      const attachmentIds = [];
      for (const file of files) {
        const saved = await patientsApi.upload(request.hospitalPatientId, {
          title: `${request.title} — ${file.name}`,
          fileName: file.name,
          dataUrl: await readFileAsDataUrl(file),
        });
        attachmentIds.push(saved.id);
      }
      const payload = { ...result };
      if (payload.values) payload.values = payload.values.filter((v) => v.test.trim()).map((v) => ({ ...v, flag: v.flag || null }));
      if (payload.dispensed) payload.dispensed = payload.dispensed.filter((d) => d.name.trim());

      const updated = await requestsApi.action(request.id, {
        action: 'complete',
        result: payload,
        resultAttachmentIds: attachmentIds.length ? attachmentIds : undefined,
      });
      success('تم اعتماد النتيجة وإضافتها للملف الطبي');
      onDone?.(updated);
      onClose();
    } catch (err) {
      toastError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`النتيجة — ${request.title}`}
      icon="file-check-2"
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            رجوع
          </Button>
          <Button variant="success" icon="check-circle-2" loading={busy} onClick={complete}>
            اعتماد وإنهاء الطلب
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {request.patient && (
          <p className="text-sm text-slate-300">
            <Icon name="user" size={14} className="inline" /> {request.patient.fullName}
          </p>
        )}

        {request.department === 'lab' && (
          <>
            <div className="space-y-2">
              <div className="hidden grid-cols-[1.5fr_1fr_0.7fr_1fr_0.8fr_auto] gap-2 px-1 text-[11px] font-bold text-slate-500 sm:grid">
                <span>التحليل</span>
                <span>النتيجة</span>
                <span>الوحدة</span>
                <span>المعدل الطبيعي</span>
                <span>التقييم</span>
                <span />
              </div>
              {result.values.map((v, i) => (
                <div key={i} className="grid grid-cols-2 gap-2 rounded-xl bg-white/[0.02] p-2 sm:grid-cols-[1.5fr_1fr_0.7fr_1fr_0.8fr_auto]">
                  <Input className="col-span-2 sm:col-span-1" value={v.test} onChange={(e) => setRow('values', i, { test: e.target.value })} placeholder="التحليل" aria-label="التحليل" />
                  <Input value={v.value} onChange={(e) => setRow('values', i, { value: e.target.value })} placeholder="النتيجة" aria-label="النتيجة" dir="ltr" />
                  <Input value={v.unit} onChange={(e) => setRow('values', i, { unit: e.target.value })} placeholder="الوحدة" aria-label="الوحدة" dir="ltr" />
                  <Input value={v.range} onChange={(e) => setRow('values', i, { range: e.target.value })} placeholder="مثال 4-11" aria-label="المعدل الطبيعي" dir="ltr" />
                  <Select value={v.flag} onChange={(e) => setRow('values', i, { flag: e.target.value })} aria-label="التقييم">
                    {FLAGS.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.label}
                      </option>
                    ))}
                  </Select>
                  <Button
                    variant="subtle"
                    icon="trash-2"
                    aria-label="حذف"
                    disabled={result.values.length === 1}
                    onClick={() => setResult((r) => ({ ...r, values: r.values.filter((_, j) => j !== i) }))}
                  />
                </div>
              ))}
              <Button variant="subtle" icon="plus" onClick={() => setResult((r) => ({ ...r, values: [...r.values, blankValue()] }))}>
                تحليل آخر
              </Button>
            </div>
            <Field label="ملخص / ملاحظات المعمل">
              <Textarea rows={2} value={result.summary} onChange={(e) => setResult((r) => ({ ...r, summary: e.target.value }))} />
            </Field>
          </>
        )}

        {request.department === 'radiology' && (
          <>
            <Field label="الانطباع (الخلاصة)" required>
              <Textarea rows={2} value={result.impression} onChange={(e) => setResult((r) => ({ ...r, impression: e.target.value }))} />
            </Field>
            <Field label="التقرير الكامل">
              <Textarea rows={6} value={result.report} onChange={(e) => setResult((r) => ({ ...r, report: e.target.value }))} />
            </Field>
          </>
        )}

        {(request.department === 'pharmacy' || request.department === 'medication') && (
          <>
            <div className="space-y-2">
              {result.dispensed.map((d, i) => (
                <div key={i} className="grid grid-cols-[1fr_6rem_auto] gap-2 rounded-xl bg-white/[0.02] p-2 sm:grid-cols-[1.4fr_0.6fr_1.4fr_auto]">
                  <Input value={d.name} onChange={(e) => setRow('dispensed', i, { name: e.target.value })} placeholder="الدواء" aria-label="الدواء" />
                  <Input value={d.quantity} onChange={(e) => setRow('dispensed', i, { quantity: e.target.value })} placeholder="الكمية" aria-label="الكمية" />
                  <Input
                    className="col-span-2 sm:col-span-1"
                    value={d.instructions}
                    onChange={(e) => setRow('dispensed', i, { instructions: e.target.value })}
                    placeholder="طريقة الاستخدام"
                    aria-label="طريقة الاستخدام"
                  />
                  <Button
                    variant="subtle"
                    icon="trash-2"
                    aria-label="حذف"
                    disabled={result.dispensed.length === 1}
                    onClick={() => setResult((r) => ({ ...r, dispensed: r.dispensed.filter((_, j) => j !== i) }))}
                  />
                </div>
              ))}
              <Button
                variant="subtle"
                icon="plus"
                onClick={() => setResult((r) => ({ ...r, dispensed: [...r.dispensed, { name: '', quantity: '1', instructions: '' }] }))}
              >
                صنف آخر
              </Button>
            </div>
            <Field label="ملاحظات الصيدلي">
              <Textarea rows={2} value={result.summary} onChange={(e) => setResult((r) => ({ ...r, summary: e.target.value }))} />
            </Field>
          </>
        )}

        {!['lab', 'radiology', 'pharmacy', 'medication'].includes(request.department) && (
          <Field label="ملخص ما تم">
            <Textarea rows={4} value={result.summary} onChange={(e) => setResult((r) => ({ ...r, summary: e.target.value }))} />
          </Field>
        )}

        {canAttach && (
          <div>
            <input ref={picker} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={pick} />
            <Button variant="subtle" icon="paperclip" onClick={() => picker.current?.click()}>
              {request.department === 'radiology' ? 'إرفاق صور الأشعة / التقرير' : 'إرفاق تقرير المعمل (PDF أو صورة)'}
            </Button>
            {files.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {files.map((f, i) => (
                  <Badge key={i} className="border-cyan-500/30 bg-cyan-500/10 text-cyan-200">
                    {f.name}
                    <button type="button" aria-label="إزالة" onClick={() => setFiles((list) => list.filter((_, j) => j !== i))}>
                      <Icon name="x" size={11} />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ResultForm;
